import React, { useEffect, useState } from 'react';

/**
 * A number field that lets the person TYPE.
 *
 * The usual `value={n} onChange={Number(e.target.value)}` pair has two
 * traps Ray hit on the shop product form (2026-09-29): clearing the field
 * turns '' into 0, so the 0 comes straight back and can only be replaced by
 * selecting it first; and typing after that 0 shows "02400". This keeps what
 * was typed as text while the field is being edited and hands the parent a
 * number (an empty field counts as 0). When the parent's value changes for
 * another reason — a different product opened — the text follows it.
 */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
   value: number | null | undefined;
   onChange: (n: number) => void;
   /** A styled input to render as, so the field keeps the form's look. */
   as?: React.ElementType;
};

const NumberInput: React.FC<Props> = ({ value, onChange, as: As = 'input', ...rest }) => {
   const [text, setText] = useState(value == null ? '' : String(value));
   useEffect(() => {
      // Follow an outside change; never fight what is being typed
      // ("2400" and "2400.0" are the same number, so leave the text alone).
      const typed = text.trim() === '' ? 0 : Number(text);
      if (value == null) {
         if (text !== '') setText('');
      } else if (typed !== value) {
         setText(String(value));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [value]);
   return (
      <As
         {...rest}
         type="number"
         inputMode="decimal"
         value={text}
         onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            setText(raw);
            const n = raw.trim() === '' ? 0 : Number(raw);
            if (Number.isFinite(n)) onChange(n);
         }}
      />
   );
};

export default NumberInput;
